import type { HTMLAttributes, KeyboardEvent, PointerEvent } from "react";

type Drag = { x0: number; from: number; width: number };
// the drag under way per handle: kept off React state, so a redraw during the drag neither loses it nor is caused by it
const drags = new WeakMap<Element, Drag>();

export type ResizeOptions = {
  /** the width set (null: the thing's own) */
  value: number | null;
  min: number;
  max: number;
  /** 1: the resized thing is left of the handle (grows to the right), -1: right of it */
  sign?: 1 | -1;
  label: string;
  help: string;
  /** the width as drawn now (when none is set) */
  current: (handle: HTMLElement) => number;
  /** shows a width while dragging (on the page, no drawing) */
  live: (handle: HTMLElement, px: number) => void;
  /** keeps a width (null: back to the thing's own) */
  commit: (px: number | null) => void;
};

/** A focusable drag handle that sets a width (WAI-ARIA window splitter): the props of its element. Dragged, the width
 *  follows the pointer (``live``) and is kept on release; the arrow keys change it by 10 px (Shift: 50), Home/End give
 *  the narrowest / widest, a double-click the thing's own width. The table's column edges and the panes' splitters. */
export function resizeHandle(o: ResizeOptions): HTMLAttributes<HTMLElement> & { "aria-valuenow"?: number } {
  const sign = o.sign ?? 1;
  const clamp = (px: number) => Math.round(Math.min(Math.max(px, o.min), o.max));
  const end = (e: PointerEvent<HTMLElement>) => {
    const d = drags.get(e.currentTarget);
    drags.delete(e.currentTarget);
    document.body.classList.remove("resizing");
    if (d && d.width !== d.from) o.commit(d.width);
  };
  return {
    role: "separator",
    "aria-orientation": "vertical",
    "aria-label": o.label,
    "aria-valuenow": o.value ?? undefined,
    "aria-valuemin": o.min,
    "aria-valuemax": o.max,
    title: o.help,
    tabIndex: 0,
    draggable: false,
    onPointerDown: (e: PointerEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation(); // not a drag of what the handle sits on (a column heading moves its column)
      e.currentTarget.setPointerCapture(e.pointerId);
      const from = o.value ?? clamp(o.current(e.currentTarget));
      drags.set(e.currentTarget, { x0: e.clientX, from, width: from });
      document.body.classList.add("resizing");
    },
    onPointerMove: (e: PointerEvent<HTMLElement>) => {
      const d = drags.get(e.currentTarget);
      if (!d) return;
      d.width = clamp(d.from + sign * (e.clientX - d.x0));
      o.live(e.currentTarget, d.width);
    },
    onPointerUp: end,
    onLostPointerCapture: end,
    onDoubleClick: () => o.commit(null),
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === "Home" || e.key === "End") {
        e.preventDefault();
        o.commit(e.key === "End" ? o.max : o.min);
        return;
      }
      const dir = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
      if (!dir) return;
      e.preventDefault();
      o.commit(clamp((o.value ?? o.current(e.currentTarget)) + sign * dir * (e.shiftKey ? 50 : 10)));
    },
  };
}
